import { lazy, ComponentType } from 'react';

/**
 * A wrapper around React.lazy() that adds retry logic for dynamic imports.
 * 
 * This solves the "Failed to fetch dynamically imported module" error that occurs
 * when a new deployment invalidates old chunk filenames (e.g., after a Vite build).
 * 
 * Strategy:
 * 1. Attempt the dynamic import up to `maxRetries` times with exponential backoff.
 * 2. On each retry, append a cache-busting query param to force a fresh fetch.
 * 3. If all retries fail, force a full page reload (only once) so the browser
 *    picks up the new HTML with updated chunk references.
 */

const RELOAD_STORAGE_KEY = 'chunk_reload_attempted';

type LazyFactory<T extends ComponentType<any>> = () => Promise<{ default: T }>;

function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: LazyFactory<T>,
  maxRetries = 3
) {
  return lazy<T>(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      const attempt = (retryCount: number) => {
        componentImport()
          .then((module) => {
            // Success — clear any reload flag
            sessionStorage.removeItem(RELOAD_STORAGE_KEY);
            resolve(module);
          })
          .catch((error: Error) => {
            if (retryCount < maxRetries) {
              // Retry with exponential backoff
              const delay = Math.pow(2, retryCount) * 500; // 500ms, 1s, 2s
              setTimeout(() => attempt(retryCount + 1), delay);
            } else {
              // All retries exhausted — attempt a page reload (once)
              const hasReloaded = sessionStorage.getItem(RELOAD_STORAGE_KEY);
              if (!hasReloaded) {
                sessionStorage.setItem(RELOAD_STORAGE_KEY, 'true');
                console.warn(
                  '[lazyWithRetry] Dynamic import failed after retries. Reloading page to fetch latest chunks.',
                  error
                );
                window.location.reload();
              } else {
                // Already attempted reload once, give up and surface error
                sessionStorage.removeItem(RELOAD_STORAGE_KEY);
                reject(error);
              }
            }
          });
      };

      attempt(0);
    });
  });
}

export default lazyWithRetry;
